import React, { useEffect, useState } from 'react';
import { LabeledValue, SelectValue } from 'antd/lib/select';
import { ColumnType } from 'antd/lib/table';
import { useRecoilValue } from 'recoil';

import {
  IngestionSourceGrade,
  IngestionSourceModelOutput,
  SmartSearchSourceSettings,
  SmartSearchToolsetSettings,
  ToolsetModel,
} from '../../../../../../gamma-api';
import IbDebounceSelect from '../../../../../components/common/IbDebounceSelect';
import { defaultApi } from '../../../../../apis';
import { getTranslateFunc } from '../../../../../utils/stringUtil';
import IbIcon from '../../../../../components/common/IbIcon';
import IbTypography from '../../../../../components/common/IbTypography';
import IbSwitch from '../../../../../components/common/IbSwitch';
import IbInput from '../../../../../components/common/IbInput';
import IbTextArea from '../../../../../components/common/IbTextArea';
import IbTabs from '../../../../../components/common/IbTabs';
import IbTabPane from '../../../../../components/common/IbTabPane';
import IbNumberInput from '../../../../../components/common/IbNumberInput';
import { tryParseIntValue } from '../../../../../utils/numberUtil';
import IbTable from '../../../../../components/common/IbTable';
import IbCheckbox from '../../../../../components/common/IbCheckbox';
import IbTooltip from '../../../../../components/common/IbTooltip';
import { getSourceTypeIconName } from '../../../../../utils/ingestionSourceUtil';
import { currentFolderIdSelector } from '../../../../../recoil';
import PromptEditorExpandable from '../../../../../components/common/PromptEditorExpandable';
import { PromptLanguage } from '../../../../../components/common/PromptEditor/const';
import { CodeEditorSize } from '../../../../../components/common/CodeEditorExpandable/types';

import {
  TABS_CLASS_NAME,
  PARAMETERS_CLASS_NAME,
  SMART_SEARCH_TOOL_NAME_EXAMPLE,
  TOOL_NAMES_PREFIX_HINT_CLASS_NAME,
  SECTION_TITLE_CLASS_NAME,
  SECTION_CLASS_NAME,
  SECTION_FORM_CLASS_NAME,
  SECTION_FORM_ITEM_CLASS_NAME,
  FORM_ITEM_CLASS_NAME,
} from './const';

const SOURCE_PAGE_SIZE = 100;

enum SmartSearchSettingsTabKey {
  Tools = 'tools',
  Sources = 'sources',
}

const getValidationState = (sources: SmartSearchSourceSettings[]) => {
  const enabledItems = sources.filter((item) => item.enabled);
  if (!enabledItems.length) return true;

  const filledLiterals = enabledItems.map((item) => item.name).filter((item) => !!item.length);
  if (enabledItems.length > filledLiterals.length) return false;

  const filledDescriptions = enabledItems.map((item) => item.description).filter((item) => !!item.length);
  if (enabledItems.length > filledDescriptions.length) return false;

  const uniqueLiterals = filledLiterals
    .map((item) => item.toLowerCase())
    .filter((item, index) => filledLiterals.indexOf(item) === index);
  return filledLiterals.length <= uniqueLiterals.length;
};

export interface ISmartSearchParametersProps {
  editToolset: ToolsetModel;
  onChange: (toolset: ToolsetModel) => void;
  onValidationStateChange: (state: boolean) => void;
}

const SmartSearchParameters: React.FC<ISmartSearchParametersProps> = ({
  editToolset,
  onChange,
  onValidationStateChange,
}) => {
  const translate = getTranslateFunc('Agent: Toolset: Modal: Settings:');

  const folderId = useRecoilValue(currentFolderIdSelector);

  const [tabKey, setTabKey] = useState(SmartSearchSettingsTabKey.Tools);
  const [selectedProject, setSelectedProject] = useState<SelectValue>();
  const [selectedProjectSources, setSelectedProjectSources] = useState<IngestionSourceModelOutput[]>([]);
  const [selectedProjectSourcesLoading, setSelectedProjectSourcesLoading] = useState(false);

  const smartSearchSettings = editToolset.settings as SmartSearchToolsetSettings;

  const fetchProjects = async (searchText: string): Promise<SelectValue[]> => {
    try {
      const response = await defaultApi.searchProjectsGeneralProjectsGet(searchText, [folderId]);
      return (response.data.items || []).map((project) => ({ value: project.project_id, label: project.name }));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      return [];
    }
  };

  const updateToolsetSettings = (projectSources: IngestionSourceModelOutput[]) => {
    const projectSourceIds = projectSources.map((projectSource) => projectSource.source_id);
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        sources: smartSearchSettings.sources.filter((source) => projectSourceIds.includes(source.source_id)),
      },
    } as ToolsetModel);
  };

  const fetchSelectedProjectSources = async (projectId: string): Promise<IngestionSourceModelOutput[] | null> => {
    setSelectedProjectSourcesLoading(true);
    let projectSources: IngestionSourceModelOutput[] | null = null;
    try {
      const response = await defaultApi.searchSourcesIngestionSourcesGet(
        undefined,
        undefined,
        [projectId],
        undefined,
        undefined,
        undefined,
        [
          IngestionSourceGrade.Previewing,
          IngestionSourceGrade.Previewed,
          IngestionSourceGrade.Releasing,
          IngestionSourceGrade.Released,
        ],
        undefined,
        0,
        SOURCE_PAGE_SIZE
      );
      projectSources = response.data.items || [];
      setSelectedProjectSources(projectSources);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setSelectedProjectSourcesLoading(false);
    }
    return projectSources;
  };

  const tryUpdateSelectedProject = async () => {
    if (!smartSearchSettings.project_id || selectedProject) {
      return;
    }

    try {
      const response = await defaultApi.getOneProjectGeneralProjectsProjectIdGet(smartSearchSettings.project_id);
      setSelectedProject({ value: response.data.project_id, label: response.data.name });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const tryUpdateSelectedProjectSources = async () => {
    if (!smartSearchSettings.project_id || selectedProjectSources.length) {
      return;
    }

    const projectSources = await fetchSelectedProjectSources(smartSearchSettings.project_id);
    if (projectSources !== null) {
      updateToolsetSettings(projectSources);
    }
  };

  const onTabChange = (activeKey: string) => {
    setTabKey(activeKey as SmartSearchSettingsTabKey);
  };

  const onProjectChange = (value: SelectValue) => {
    const projectId = (value as LabeledValue).value as string;
    setSelectedProject(value);
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        project_id: projectId,
        sources: [],
      },
    } as ToolsetModel);
    onValidationStateChange(true);
    fetchSelectedProjectSources(projectId).finally();
  };

  const onToolNamesPrefixChange = (value: string) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        tool_names_prefix: value,
      },
    } as ToolsetModel);
  };

  const onQueryToolEnabledChange = (checked: boolean) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        query_tool_settings: {
          ...smartSearchSettings.query_tool_settings,
          enabled: checked,
        },
      },
    } as ToolsetModel);
  };

  const onQueryToolDescriptionChange = (value: string) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        query_tool_settings: {
          ...smartSearchSettings.query_tool_settings,
          tool_description: value,
        },
      },
    } as ToolsetModel);
  };

  const onSearchDocumentsToolEnabledChange = (checked: boolean) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        search_documents_tool_settings: {
          ...smartSearchSettings.search_documents_tool_settings,
          enabled: checked,
        },
      },
    } as ToolsetModel);
  };

  const onSearchDocumentsToolDescriptionChange = (value: string) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        search_documents_tool_settings: {
          ...smartSearchSettings.search_documents_tool_settings,
          tool_description: value,
        },
      },
    } as ToolsetModel);
  };

  const onDownloadDocumentToolEnabledChange = (checked: boolean) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        download_document_tool_settings: {
          ...smartSearchSettings.download_document_tool_settings,
          enabled: checked,
        },
      },
    } as ToolsetModel);
  };

  const onDownloadDocumentToolDescriptionChange = (value: string) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        download_document_tool_settings: {
          ...smartSearchSettings.download_document_tool_settings,
          tool_description: value,
        },
      },
    } as ToolsetModel);
  };

  const onDownloadDocumentToolMaxContentTokenCountChange = (value: string | number) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        download_document_tool_settings: {
          ...smartSearchSettings.download_document_tool_settings,
          max_content_token_count: tryParseIntValue(value) ?? 0,
        },
      },
    } as ToolsetModel);
  };

  const onSourcesChange = (sources: SmartSearchSourceSettings[]) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        sources,
      },
    } as ToolsetModel);
    onValidationStateChange(getValidationState(sources));
  };

  const onProjectIdChange = () => {
    tryUpdateSelectedProject().finally();
    tryUpdateSelectedProjectSources().finally();
  };
  useEffect(onProjectIdChange, [smartSearchSettings.project_id]);

  const renderProjectSettings = () => (
    <div className={FORM_ITEM_CLASS_NAME}>
      <IbTypography.Paragraph strong type="secondary">
        {translate('Project: Title')}
      </IbTypography.Paragraph>
      <IbDebounceSelect showSearch fetchOptions={fetchProjects} value={selectedProject} onChange={onProjectChange} />
    </div>
  );

  const renderToolNamesPrefixSettings = () => (
    <div className={FORM_ITEM_CLASS_NAME}>
      <IbTypography.Paragraph strong type="secondary">
        {translate('Tool names prefix: Title')}
      </IbTypography.Paragraph>
      <IbInput value={smartSearchSettings.tool_names_prefix} onChange={onToolNamesPrefixChange} />
      {smartSearchSettings.tool_names_prefix && (
        <div className={TOOL_NAMES_PREFIX_HINT_CLASS_NAME}>
          <IbTypography.Paragraph disabled strong type="secondary">
            {translate('Tool names prefix: Example')}
          </IbTypography.Paragraph>
          <IbTooltip title={`${smartSearchSettings.tool_names_prefix}${SMART_SEARCH_TOOL_NAME_EXAMPLE}`}>
            <IbTypography.Paragraph ellipsis type="secondary">
              {`${smartSearchSettings.tool_names_prefix}${SMART_SEARCH_TOOL_NAME_EXAMPLE}`}
            </IbTypography.Paragraph>
          </IbTooltip>
        </div>
      )}
    </div>
  );

  const renderTools = () => {
    return (
      <div className={PARAMETERS_CLASS_NAME}>
        <div className={SECTION_CLASS_NAME}>
          <div className={SECTION_TITLE_CLASS_NAME}>
            <IbSwitch
              checked={smartSearchSettings.query_tool_settings.enabled}
              size="small"
              onClick={onQueryToolEnabledChange}
            />
            <IbIcon iconName="search" size={20} />
            <IbTypography.Paragraph strong>{translate('Project: Query tool settings: Title')}</IbTypography.Paragraph>
          </div>
          {smartSearchSettings.query_tool_settings.enabled && (
            <div className={SECTION_FORM_CLASS_NAME}>
              <div className={SECTION_FORM_ITEM_CLASS_NAME}>
                <IbTypography.Paragraph strong type="secondary">
                  {translate('Project: Query tool settings: Tool description: Title')}
                </IbTypography.Paragraph>
                <IbTypography.Paragraph type="secondary">
                  {translate('Project: Query tool settings: Tool description: Description')}
                </IbTypography.Paragraph>
                <PromptEditorExpandable
                  promptLanguage={PromptLanguage.PLAIN_TEXT}
                  size={CodeEditorSize.SM}
                  value={smartSearchSettings.query_tool_settings.tool_description}
                  onChange={onQueryToolDescriptionChange}
                />
              </div>
            </div>
          )}
        </div>
        <div className={SECTION_CLASS_NAME}>
          <div className={SECTION_TITLE_CLASS_NAME}>
            <IbSwitch
              checked={smartSearchSettings.search_documents_tool_settings.enabled}
              size="small"
              onClick={onSearchDocumentsToolEnabledChange}
            />
            <IbIcon iconName="file-search-two" size={20} />
            <IbTypography.Paragraph strong>
              {translate('Project: Search documents tool settings: Title')}
            </IbTypography.Paragraph>
          </div>
          {smartSearchSettings.search_documents_tool_settings.enabled && (
            <div className={SECTION_FORM_CLASS_NAME}>
              <div className={SECTION_FORM_ITEM_CLASS_NAME}>
                <IbTypography.Paragraph strong type="secondary">
                  {translate('Project: Search documents tool settings: Tool description: Title')}
                </IbTypography.Paragraph>
                <IbTypography.Paragraph type="secondary">
                  {translate('Project: Search documents tool settings: Tool description: Description')}
                </IbTypography.Paragraph>
                <PromptEditorExpandable
                  promptLanguage={PromptLanguage.PLAIN_TEXT}
                  size={CodeEditorSize.SM}
                  value={smartSearchSettings.search_documents_tool_settings.tool_description}
                  onChange={onSearchDocumentsToolDescriptionChange}
                />
              </div>
            </div>
          )}
        </div>
        <div className={SECTION_CLASS_NAME}>
          <div className={SECTION_TITLE_CLASS_NAME}>
            <IbSwitch
              checked={smartSearchSettings.download_document_tool_settings.enabled}
              size="small"
              onClick={onDownloadDocumentToolEnabledChange}
            />
            <IbIcon iconName="file-success" size={20} />
            <IbTypography.Paragraph strong>
              {translate('Project: Download document tool settings: Title')}
            </IbTypography.Paragraph>
          </div>
          {smartSearchSettings.download_document_tool_settings.enabled && (
            <div className={SECTION_FORM_CLASS_NAME}>
              <div className={SECTION_FORM_ITEM_CLASS_NAME}>
                <IbTypography.Paragraph strong type="secondary">
                  {translate('Project: Download document tool settings: Tool description: Title')}
                </IbTypography.Paragraph>
                <IbTypography.Paragraph type="secondary">
                  {translate('Project: Download document tool settings: Tool description: Description')}
                </IbTypography.Paragraph>
                <PromptEditorExpandable
                  promptLanguage={PromptLanguage.PLAIN_TEXT}
                  size={CodeEditorSize.SM}
                  value={smartSearchSettings.download_document_tool_settings.tool_description}
                  onChange={onDownloadDocumentToolDescriptionChange}
                />
              </div>
              <div className={SECTION_FORM_ITEM_CLASS_NAME}>
                <IbTypography.Paragraph strong type="secondary">
                  {translate('Project: Download document tool settings: Max content length: Title')}
                </IbTypography.Paragraph>
                <IbNumberInput
                  min={0}
                  precision={0}
                  value={smartSearchSettings.download_document_tool_settings.max_content_token_count}
                  onChange={onDownloadDocumentToolMaxContentTokenCountChange}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSourceSelectionCell = (projectSource: IngestionSourceModelOutput) => {
    const source = smartSearchSettings.sources.find((source) => source.source_id === projectSource.source_id);
    const sourceChecked = !!source && source.enabled;

    const onSourceCheckedChange = (value: boolean) => {
      if (value && source) {
        onSourcesChange(
          smartSearchSettings.sources.map((item) =>
            source.source_id === item.source_id ? { ...item, enabled: true } : item
          )
        );
      } else if (value && !source) {
        onSourcesChange([
          ...smartSearchSettings.sources,
          { source_id: projectSource.source_id, enabled: true, name: '', description: '' },
        ]);
      } else if (!value && source) {
        onSourcesChange(
          smartSearchSettings.sources.map((item) =>
            source.source_id === item.source_id ? { ...item, enabled: false } : item
          )
        );
      }
    };

    return <IbCheckbox value={sourceChecked} onChange={onSourceCheckedChange} />;
  };

  const renderSourceNameCell = (projectSource: IngestionSourceModelOutput) => (
    <IbTypography.Paragraph type="secondary">
      <IbIcon iconName={getSourceTypeIconName(projectSource.versions.latest.source.parameters.type)} size={20} />
      {projectSource.name}
    </IbTypography.Paragraph>
  );

  const renderLiteralCell = (projectSource: IngestionSourceModelOutput) => {
    const source = smartSearchSettings.sources.find((source) => source.source_id === projectSource.source_id);

    if (!source) return null;

    if (!source.enabled) {
      return <IbTypography.Paragraph type="secondary">{source.name}</IbTypography.Paragraph>;
    }

    const sourceName = source.name.trim();
    const errorMessage = sourceName
      ? smartSearchSettings.sources.find(
          (item) => item.enabled && item.source_id !== source.source_id && item.name === sourceName
        )
        ? translate('Project: Validation: Duplicate')
        : null
      : translate('Project: Validation: Required');

    const onInputChange = (value: string) => {
      onSourcesChange(
        smartSearchSettings.sources.map((source) =>
          source.source_id === projectSource.source_id
            ? {
                ...source,
                name: value,
              }
            : source
        )
      );
    };

    return (
      <>
        <IbInput status={errorMessage ? 'error' : 'default'} value={source.name} onChange={onInputChange} />
        {errorMessage ? (
          <IbTypography.Paragraph error type="descriptor">
            {errorMessage}
          </IbTypography.Paragraph>
        ) : null}
      </>
    );
  };

  const renderDescriptionCell = (projectSource: IngestionSourceModelOutput) => {
    const source = smartSearchSettings.sources.find((source) => source.source_id === projectSource.source_id);

    if (!source) return null;

    if (!source.enabled) {
      return <IbTypography.Paragraph type="secondary">{source.description}</IbTypography.Paragraph>;
    }

    const errorMessage = source.description.trim() ? null : translate('Project: Validation: Required');

    const onInputChange = (value: string) => {
      onSourcesChange(
        smartSearchSettings.sources.map((source) =>
          source.source_id === projectSource.source_id
            ? {
                ...source,
                description: value,
              }
            : source
        )
      );
    };

    return (
      <>
        <IbTextArea status={errorMessage ? 'error' : 'default'} value={source.description} onChange={onInputChange} />
        {errorMessage ? (
          <IbTypography.Paragraph error type="descriptor">
            {errorMessage}
          </IbTypography.Paragraph>
        ) : null}
      </>
    );
  };

  const renderSources = () => {
    const columns: ColumnType<IngestionSourceModelOutput>[] = [
      {
        title: '',
        render: renderSourceSelectionCell,
        width: '3%',
      },
      {
        title: translate('Project: Sources table: Source'),
        render: renderSourceNameCell,
        width: '40%',
      },
      {
        title: translate('Project: Sources table: Literal'),
        render: renderLiteralCell,
        width: '15%',
      },
      {
        title: translate('Project: Sources table: Description'),
        render: renderDescriptionCell,
      },
    ];

    const emptyText = translate('Project: Sources table: Empty text');

    return (
      <IbTable
        columns={columns}
        dataSource={selectedProjectSources}
        emptyText={emptyText}
        loading={selectedProjectSourcesLoading}
        rowKey="entity_id"
      />
    );
  };

  const renderTabs = () => (
    <IbTabs activeKey={tabKey} className={TABS_CLASS_NAME} onChange={onTabChange}>
      <IbTabPane
        key={SmartSearchSettingsTabKey.Tools}
        tab={translate('Project: Tabs: Tools: Title')}
        tabKey={SmartSearchSettingsTabKey.Tools}
      >
        {renderTools()}
      </IbTabPane>
      <IbTabPane
        key={SmartSearchSettingsTabKey.Sources}
        tab={translate('Project: Tabs: Sources: Title')}
        tabKey={SmartSearchSettingsTabKey.Sources}
      >
        {renderSources()}
      </IbTabPane>
    </IbTabs>
  );

  return (
    <div className={PARAMETERS_CLASS_NAME}>
      {renderProjectSettings()}
      {renderToolNamesPrefixSettings()}
      {renderTabs()}
    </div>
  );
};

export default SmartSearchParameters;
